import React, { useCallback } from 'react'
import {
  IReportComponentInstance,
  IInstructionComponentInstance,
  IInstructionLookUpEntry,
  ReportComponentType,
} from 'src/types/report-designer'
import { IDropdownDto } from 'src/generated-types/api-result'
import { ReportTemplateStatus } from 'src/enums'
import {
  buildReportTemplate as buildReportTemplateAction,
  createInstance,
  handleCanvasDropLogic,
  handleCanvasDropAtIndexLogic,
  handleReorderLogic,
  handleDeleteItemLogic,
  handleUpdateItemLogic,
  handleSaveDraftAction,
  handleSaveReportAction,
  handleStatusChangeAction,
  handleUpgradeAction,
} from './ReportDesignerContainer.action'
import { useReportDesignerState } from './useReportDesignerState'

export interface UseReportDesignerParams {
  customerId: number
  templateId?: EntityId
  templateName?: string
  locations?: number[]
  formSchema?: IDropdownDto
}

export const useReportDesigner = (params: UseReportDesignerParams) => {
  const { customerId } = params
  const {
    translate,
    theme,
    isEditMode,
    initialData,
    isTemplateLoading,
    formSchemaData,
    formSchemaColumns,
    isColumnsLoading,
    warnings,
    changeStatus,
    upgradeTemplate,
    closeAllDialogs,
    showConfirmDialog,
    openFilePreviewDialog,
    saveReportTemplate,
    updateReportTemplate,
    previewReport,
    isPreviewLoading,
    canvasItems,
    setCanvasItems,
    selectedItemId,
    setSelectedItemId,
    instructionLookUp,
    setInstructionLookUp,
    currentTemplateName,
    setCurrentTemplateName,
    currentLocations,
    setCurrentLocations,
    isUpgradeLoading,
  } = useReportDesignerState(params)

  const handleLookUpChange = useCallback(
    (updater: (prev: Record<string, IInstructionLookUpEntry>) => Record<string, IInstructionLookUpEntry>) => {
      setInstructionLookUp(updater)
    },
    [setInstructionLookUp],
  )

  const buildReportTemplate = useCallback(
    (status: ReportTemplateStatus) =>
      buildReportTemplateAction(
        {
          isEditMode,
          initialData,
          currentTemplateName,
          canvasItems,
          customerId,
          currentLocations,
          formSchemaDataId: formSchemaData?.id,
          instructionLookUp,
        },
        status,
      ),
    [
      canvasItems,
      customerId,
      currentLocations,
      currentTemplateName,
      formSchemaData,
      initialData,
      isEditMode,
      instructionLookUp,
    ],
  )

  const handlePreview = async () => {
    const template = buildReportTemplate(ReportTemplateStatus.Draft)
    await previewReport(
      {
        layout: template.layout,
        formSchemaVersionId: initialData?.formSchemaVersionId ?? formSchemaData?.id,
        formSchemaId: formSchemaData?.formSchemaId ?? 0,
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      {
        onSuccess: res => {
          const blob = new Blob([res.data as ArrayBuffer], { type: 'application/pdf' })
          const blobUrl = URL.createObjectURL(blob)
          openFilePreviewDialog(blobUrl, undefined, undefined, 'pdf')
        },
      },
    )
  }

  const handleSaveDraft = async () => {
    const template = buildReportTemplate(ReportTemplateStatus.Draft)
    const mutate = isEditMode ? updateReportTemplate : saveReportTemplate
    await handleSaveDraftAction({ template, isEditMode, customerId, mutateAsync: mutate, translate, closeAllDialogs })
  }

  const handleSaveReport = async () => {
    const template = buildReportTemplate(
      isEditMode
        ? initialData?.status == ReportTemplateStatus.Warning
          ? initialData?.status
          : initialData?.status === ReportTemplateStatus.Draft &&
            warnings &&
            warnings.removedInLatest.length > 0 &&
            !warnings?.isLatestVersion
          ? ReportTemplateStatus.Warning
          : ReportTemplateStatus.Active
        : ReportTemplateStatus.Active,
    )
    const mutate = isEditMode ? updateReportTemplate : saveReportTemplate
    await handleSaveReportAction({ template, isEditMode, customerId, mutateAsync: mutate, translate, closeAllDialogs })
  }

  const createInstanceCallback = useCallback(
    (type: ReportComponentType) => createInstance(type, { translate, theme }),
    [translate, theme],
  )

  const handleCanvasDrop = useCallback(
    (e: React.DragEvent) => {
      const componentType = Number(e.dataTransfer.getData('componentType')) as ReportComponentType
      if (!componentType) return
      const newInstance = createInstanceCallback(componentType)
      setCanvasItems(handleCanvasDropLogic(componentType, canvasItems, newInstance))
      setSelectedItemId(newInstance.id)
    },
    [canvasItems, createInstanceCallback, setCanvasItems, setSelectedItemId],
  )

  const handleCanvasDropAtIndex = useCallback(
    (e: React.DragEvent, insertIndex: number) => {
      const componentType = Number(e.dataTransfer.getData('componentType')) as ReportComponentType
      if (!componentType) return
      const newInstance = createInstanceCallback(componentType)
      setCanvasItems(handleCanvasDropAtIndexLogic(componentType, canvasItems, newInstance, insertIndex))
      setSelectedItemId(newInstance.id)
    },
    [canvasItems, createInstanceCallback, setCanvasItems, setSelectedItemId],
  )

  const handleSelectItem = useCallback((id: string) => setSelectedItemId(id), [setSelectedItemId])

  const handleUpdateItem = useCallback(
    (updated: IReportComponentInstance) => setCanvasItems(prev => handleUpdateItemLogic(prev, updated)),
    [setCanvasItems],
  )

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => setCanvasItems(prev => handleReorderLogic(prev, fromIndex, toIndex)),
    [setCanvasItems],
  )

  const handleDeleteItem = useCallback(
    (id: string) => {
      const itemToDelete = canvasItems.find(item => item.id === id)
      setCanvasItems(prev => handleDeleteItemLogic(prev, id))

      if (itemToDelete?.type === ReportComponentType.Instruction) {
        const instructionInstance = itemToDelete as IInstructionComponentInstance
        setInstructionLookUp(prev => {
          const updated = { ...prev }
          for (const col of instructionInstance.columns) {
            if (col.formSchemaFieldId) delete updated[col.formSchemaFieldId]
          }
          return updated
        })
      }

      if (selectedItemId === id) setSelectedItemId(null)
    },
    [selectedItemId, canvasItems, setCanvasItems, setInstructionLookUp, setSelectedItemId],
  )

  const handleStatusChange = async (newStatus: ReportTemplateStatus) => {
    if (!isEditMode || !customerId || !initialData?.id) return
    await handleStatusChangeAction({
      customerId,
      initialData,
      newStatus,
      formSchemaId: formSchemaData?.formSchemaId ?? 0,
      mutateAsync: changeStatus,
      translate,
    })
  }

  const selectedItem = canvasItems.find(item => item.id === selectedItemId) ?? null
  const hasTopBar = canvasItems.some(item => item.type === ReportComponentType.TopBar)

  const warningMessage = (() => {
    if (!warnings || warnings.isLatestVersion) return ''
    const parts = [translate('version-upgrade-warning-intro', { ns: 'dynamic-report' })]
    if (!warnings?.isLatestVersion && warnings.removedInLatest?.length > 0)
      parts.push(translate('version-upgrade-warning-removed', { ns: 'dynamic-report' }))
    parts.push(translate('version-upgrade-warning-footer', { ns: 'dynamic-report' }))
    return parts.join(' ')
  })()

  const handleUpgrade = useCallback(() => {
    showConfirmDialog(
      translate('upgrade-report', { ns: 'dynamic-report' }),
      warningMessage,
      { acceptText: translate('yes', { ns: 'common' }), cancelText: translate('no', { ns: 'common' }) },
      async confirmPop => {
        await handleUpgradeAction({
          customerId,
          initialData,
          mutateAsync: config => upgradeTemplate(undefined, config),
          translate,
          closeAllDialogs,
        })
        confirmPop()
      },
    )
  }, [warningMessage, customerId, initialData, upgradeTemplate, translate, closeAllDialogs, showConfirmDialog])

  return {
    canvasItems,
    selectedItemId,
    currentTemplateName,
    currentLocations,
    setCurrentTemplateName,
    setCurrentLocations,
    initialData,
    isTemplateLoading,
    formSchemaColumns,
    isColumnsLoading,
    warnings,
    isPreviewLoading,
    selectedItem,
    hasTopBar,
    warningMessage,
    isEditMode,
    handlePreview,
    handleSaveDraft,
    handleSaveReport,
    handleCanvasDrop,
    handleCanvasDropAtIndex,
    handleSelectItem,
    handleUpdateItem,
    handleReorder,
    handleDeleteItem,
    handleStatusChange,
    handleUpgrade,
    handleLookUpChange,
    isUpgradeLoading,
  }
}
